import React, { useState, useEffect } from "react";
import { BookOpen } from "lucide-react";

/**
 * <SmartImage /> — an <img> with a graceful onError fallback that swaps to a
 * Folio-branded placeholder (never a broken grey box).
 *
 * Props: same as <img> plus `containerClassName` for the fallback wrapper.
 */
export default function SmartImage({
  src,
  alt = "",
  className = "",
  containerClassName = "",
  ...rest
}) {
  const [failed, setFailed] = useState(false);

  // Reset error state whenever the src changes so a valid URL after a failed one
  // gets a second chance.
  useEffect(() => { setFailed(false); }, [src]);

  if (!src || failed) {
    return (
      <div
        data-testid="smart-image-fallback"
        className={`w-full h-full bg-linen flex items-center justify-center ${containerClassName}`}
        role="img"
        aria-label={alt || "Folio placeholder"}
      >
        <div className="flex items-center gap-1.5 opacity-50">
          <BookOpen className="w-6 h-6 text-terracotta" strokeWidth={1.25} />
          <span className="font-serif text-xl text-ink tracking-tight">Folio</span>
        </div>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setFailed(true)}
      {...rest}
    />
  );
}
